import React, { useCallback, useRef, useState } from 'react';
import useModal from './useModal';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { InputText, Textarea } from '@/components/form';
import { Button, Typography } from '@/components/common';
import { errorFieldMessage } from '@/const';
import {
	FollowUpQuestion,
	FollowUpQuestionPanel,
} from '@/components/screens/lawyerapp/cases/case-administration/applicant-intake-phase/FollowUpQuestionPanel';
import { CaseContext } from '@/context/CaseContext';
import { EmailLanguageModal } from '@/components/modals/EmailLanguageModal';
import { SurveyQuestionsModal } from '@/components/modals/survey/SurveyQuestionsModal';
import {
	AddFollowUpPayload,
	CaseQuestionI,
	FollowUpTagData,
} from '@/interfaces';
import { FollowUpPreview } from '@/components/screens/lawyerapp/cases/email-preview/FollowUp';
import useModalPreviewPage from './useModalPreviewPage';
import useDeviceWidth from './useDeviceWidth';
import { format } from 'date-fns';

interface UseSendFollowUpProps {
	onSubmit?: (data: AddFollowUpPayload) => void;
	isSendFromAdministration?: boolean;
}

export const useSendFollowUp = ({
	onSubmit: onSubmitProp,
	isSendFromAdministration = false,
}: UseSendFollowUpProps = {}) => {
	const {
		register,
		handleSubmit,
		reset,
		setValue,
		watch,
		formState: { errors },
	} = useForm();

	const { caseDetails } = React.useContext(CaseContext);
	const width = useDeviceWidth();

	// ── Modal states ──────────────────────────────────────────────────────────
	const [isEmailOpen, setIsEmailOpen] = useState(false);
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const previewFromEmail = useRef(false);

	// ── Questions state from the panel ────────────────────────────────────────
	const [followUpQuestions, setFollowUpQuestions] = useState<
		FollowUpQuestion[]
	>([]);
	const [isFollowUpValid, setIsFollowUpValid] = useState(false);
	const [emailText, setEmailText] = useState('');

	// Map FollowUpQuestion[] → CaseQuestionI[] for the SurveyQuestionsModal
	const mappedQuestionsForPreview: CaseQuestionI[] = followUpQuestions.map(
		(q, index) => ({
			id: index,
			question: q.question || '',
			question_type: q.question_type,
			ask_explanation: q.ask_explanation,
			multiple_choise_questions: (q.expected_answers || []).map((a, i) => ({
				id: i,
				brand: a.label || a.value || '',
				complement: a.detail || '',
			})),
		})
	);

	const rules = {
		follow_up_name: {
			required: errorFieldMessage,
		},
	};

	const {
		Modal: ModalSendFollowUp,
		show: showModalSendFollowUp,
		hide: hideModalSendFollowUp,
	} = useModal();

	const { ModalPreviewPage, hidePreviewPage, showPreviewPage } =
		useModalPreviewPage();

	const [followUpId, setFollowUpId] = useState<number | undefined>(undefined);

	const onOpenSendFollowUp = useCallback(
		(initialData?: FollowUpTagData) => {
			reset();
			if (initialData) {
				setFollowUpId(initialData.id);
				setValue('follow_up_name', initialData.name || '');
				setValue('heading', initialData.heading || 'Instructions');
				setValue(
					'description',
					initialData.description || 'Please answer the following questions.'
				);
				setEmailText(initialData.email_text || '');

				// Map API questions (FollowUpList / QuestionItem) to FollowUpQuestion format
				const mappedQuestions: FollowUpQuestion[] = (
					initialData.questions || []
				).map((q) => {
					let expected_answer =
						q.options?.find((o) => o.is_correct)?.text || '';
					if (q.type === 'DATE') {
						expected_answer =
							q.options?.map((o) => o.text.replaceAll('-', '/')).join(' - ') ||
							'';
					} else if (q.type === 'YES_NO') {
						const yesCorrect = q.options?.find(
							(o) => o.text === 'Yes'
						)?.is_correct;
						const noCorrect = q.options?.find(
							(o) => o.text === 'No'
						)?.is_correct;
						expected_answer =
							yesCorrect && noCorrect ? 'BOTH' : yesCorrect ? 'YES' : 'NO';
					}

					return {
						question_type: q.type,
						question: q.text,
						expected_answer,
						expected_answers: (q.options || []).map((o) => ({
							value: o.text,
							label: o.text,
							detail: '',
							isCorrect: o.is_correct,
						})),
						auto_approve: q.automatic_approval ?? false,
						ask_explanation: q.explanation ?? false,
					};
				});
				setFollowUpQuestions(mappedQuestions);
			} else {
				setFollowUpId(undefined);
				setFollowUpQuestions([]);
				setEmailText('');
				setValue('heading', 'Instructions');
				setValue('description', 'Please answer the following questions.');
			}
			showModalSendFollowUp();
		},
		[reset, showModalSendFollowUp, setValue]
	);

	const onSubmit: SubmitHandler<FieldValues> = async (data) => {
		console.log(data);
	};

	// ── Navigation handlers ───────────────────────────────────────────────────

	const handlePreview = () => {
		previewFromEmail.current = false;
		hideModalSendFollowUp();
		setIsPreviewOpen(true);
	};

	const handleContinue = () => {
		hideModalSendFollowUp();
		setIsEmailOpen(true);
	};

	const handleEmailPreview = () => {
		previewFromEmail.current = true;
		setIsEmailOpen(false);
		showPreviewPage();
	};

	const handlePreviewClose = () => {
		setIsPreviewOpen(false);
		if (previewFromEmail.current) {
			previewFromEmail.current = false;
			setIsEmailOpen(true);
		} else {
			showModalSendFollowUp();
		}
	};

	const handleBackEmailPreview = () => {
		hidePreviewPage();
		setIsEmailOpen(true);
	};

	const handleFinalSubmit = (emailText: string) => {
		if (onSubmitProp) {
			const finalData: AddFollowUpPayload = {
				case_id: caseDetails?.id || '',
				name: watch('follow_up_name'),
				heading: watch('heading'),
				description: watch('description'),
				email_text: emailText,
				follow_up_questions: followUpQuestions.map((q) => {
					let options: {
						text: string;
						is_correct: boolean;
						details?: string;
					}[] = [];

					if (q.question_type === 'DATE') {
						// expected_answer holds the range as "MM/DD/YYYY-MM/DD/YYYY"
						const parts = (q.expected_answer || '').split('-');
						const dateStart = parts[0]?.trim()
							? format(parts[0]?.trim(), 'MM-dd-yyyy')
							: '';
						const dateEnd = parts[1]?.trim()
							? format(parts[1]?.trim(), 'MM-dd-yyyy')
							: '';
						options = [
							{ text: dateStart, is_correct: true },
							{ text: dateEnd, is_correct: true },
						];
					} else if (q.question_type === 'YES_NO') {
						// expected_answer is 'YES' | 'NO' | 'BOTH'
						const answer = q.expected_answer ?? '';
						options = [
							{
								text: 'Yes',
								is_correct: answer === 'YES' || answer === 'BOTH',
							},
							{ text: 'No', is_correct: answer === 'NO' || answer === 'BOTH' },
						];
					} else {
						options = (q.expected_answers || []).map((a) => ({
							details: a.detail,
							is_correct: a.isCorrect,
							text: a.label || a.value,
						}));
					}

					return {
						automatic_approval: q.auto_approve,
						explanation: q.ask_explanation,
						type: q.question_type,
						text: q.question,
						options,
					};
				}) as any, // backend expects specific Options structure but we conform to the payload questions
			};

			if (followUpId) {
				finalData.id = followUpId;
			}

			onSubmitProp(finalData);
			setIsEmailOpen(false);
			hideModalSendFollowUp();
		}
	};

	const renderSendFollowUpModals = (
		<>
			<EmailLanguageModal
				isOpen={isEmailOpen}
				onClose={() => setIsEmailOpen(false)}
				onBack={() => {
					setIsEmailOpen(false);
					showModalSendFollowUp();
				}}
				onPreview={handleEmailPreview}
				onSubmit={handleFinalSubmit}
				emailText={emailText}
				onEmailTextChange={setEmailText}
				register={register}
				isSendFromAdministration={isSendFromAdministration}
			/>
			<SurveyQuestionsModal
				isPreview
				title={watch('follow_up_name')}
				questions={mappedQuestionsForPreview}
				isOpen={isPreviewOpen}
				heading={watch('heading')}
				description={watch('description')}
				onClose={handlePreviewClose}
			/>
			<ModalPreviewPage
				handleClickBack={handleBackEmailPreview}
				title="This is your preview page."
				hideApproveButton={true}
				hideEditButton={true}
			>
				<FollowUpPreview
					caseName={caseDetails?.title || 'Case Name'}
					followUpName={watch('follow_up_name')}
					emailMessage={emailText}
				/>
			</ModalPreviewPage>
			<ModalSendFollowUp
				title="Create Follow-up"
				backgroundColor="white"
				classNameCardContainer="w-full 2xl:!w-[1182px] !px-0 !py-0 !pt-8"
				classNameContainerChildren="!px-0 !py-0 h-[85vh] 2xl:h-[80vh]"
				classNameTitle="pl-8 text-left !mb-4"
				withXButton
				noMaxHeight
			>
				<form
					onSubmit={handleSubmit(onSubmit)}
					className="flex flex-col md:flex-row h-full overflow-hidden rounded-b-[32px]"
				>
					{/* ── Left panel: Follow-up details ── */}
					<div
						style={{ width: width < 768 ? '100%' : '340px' }}
						className="h-full flex flex-col gap-4 bg-grey-5 pt-4 pb-8 pl-8 pr-6 border-t border-grey-100"
					>
						<div>
							<Typography
								type="semibold-16"
								className="text-black text-left mb-2"
							>
								Follow-up Details
							</Typography>
							<InputText
								name="follow_up_name"
								placeholder="Follow-up [Date]"
								title="Follow-up Name"
								register={register}
								error={errors.follow_up_name}
								rules={rules.follow_up_name}
							/>
						</div>
						<div>
							<Typography type="semibold-16" className="text-black text-left">
								Instructions
							</Typography>
							<Typography
								type="regular-12"
								className="text-opacity-black text-left mb-2"
							>
								Provide context for the questions.
							</Typography>
							<InputText
								name="heading"
								placeholder="Instructions"
								title="Heading"
								register={register}
							/>
							<Typography
								type="regular-16"
								className="text-black text-left mt-4"
							>
								Description / Context
							</Typography>
							<Typography
								type="regular-12"
								className="text-opacity-black text-left"
							>
								This text will be shown to clients before they start answering
								the questions.
							</Typography>
							<Textarea
								name="description"
								register={register}
								rows={8}
								classNameContainerInput="!border-gray-disabled"
								placeholder="Please answer the following questions."
							/>
						</div>
					</div>

					{/* ── Right panel: Questions ── */}
					<div
						style={{ width: width < 1250 ? '100%' : '906px' }}
						className="relative lg:min-w-[800px] flex flex-col border border-grey-100 bg-grey-2"
					>
						<div className="flex-1 overflow-hidden">
							<FollowUpQuestionPanel
								caseDetails={caseDetails}
								initialQuestions={followUpQuestions}
								onQuestionsChange={setFollowUpQuestions}
								onValidChange={setIsFollowUpValid}
							/>
						</div>

						<div className="flex flex-col md:flex-row  w-full justify-between gap-3 p-4 border-t border-grey-100">
							<Button
								label="Preview"
								size="fit"
								decoration="secondary"
								onClick={handlePreview}
							/>
							<Button
								type="submit"
								label="Continue"
								size="fit"
								decoration="primary"
								onClick={handleContinue}
								disabled={!isFollowUpValid || !watch('follow_up_name')}
							/>
						</div>
					</div>
				</form>
			</ModalSendFollowUp>
		</>
	);

	return {
		onOpenSendFollowUp,
		renderSendFollowUpModals,
		hideModalSendFollowUp,
	};
};
